import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";

//Bootstrap
import { Card, Form } from "react-bootstrap";

//API calls
import {
  useGetSifrantVlogaPartnerja,
  useAddClanicaUMToProject,
} from "../../../../services/ProjectDetails/partnerskeOrganizacijeAPI";
import {
  useGetOsebeFromImenik,
  useGetClaniceSifrant,
} from "../../../../services/Shared/sharedAPI";

//UI components
import FormSelect from "../../../ui/FormSelect";
import FormSelectSearchAsyncField from "../../../ui/FormSelectSearchAsyncField";
import ErrorMessage from "../../../ui/ErrorMessage";
import FormField from "../../../ui/FormField";

//Utils
import { useDebounceHook } from "../../../utils/useDebounceHook";
import sifrantSetSelectValueAndLabel from "../../../utils/sifrantSetSelectValueAndLabel";

//Validation schema
import formAddClanicaUMSchema from "../../../../config/formAddClanicaUM";

const AddClanicoNaProjekt = ({ setIsAdding, projectClanice }) => {
  const { id } = useParams();

  const [searchTermOseba, setSearchTermOseba] = useState("");

  const schema = useMemo(() => formAddClanicaUMSchema, []);

  //GET vloge za partnerja
  const { data: sifrantVloga } = useGetSifrantVlogaPartnerja(true);

  //GET clanice UM sifrant
  const { data: sifrantClanice } = useGetClaniceSifrant();

  //GET osebe from Imenik based on search term
  const { data: osebeImenik, isLoading: isLoadingOsebe } =
    useGetOsebeFromImenik(useDebounceHook(searchTermOseba, 300));

  //POST call to add clanica to project
  const {
    mutate: addClanicaNaProjekt,
    isPending: isPendingAddClanicaNaProjekt,
    isError: isError,
    error,
    reset: resetMutationError,
  } = useAddClanicaUMToProject(id);

  //INIT form with react-hook-form and zod validation
  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    register,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      clanicaID: null,
      vlogaID: null,
      osebaSelected: null,
      skrbnik: false,
    },
  });

  //setSifrantVlogaOptions in format for select component
  const sifrantVlogaOptions = sifrantSetSelectValueAndLabel(sifrantVloga);

  //set clanice options and disable in select those that are already added to the project
  const claniceOptions = sifrantSetSelectValueAndLabel(sifrantClanice).map(
    (option) => ({
      ...option,
      isDisabled: projectClanice.some(
        (clanica) => clanica.clanicaID === option.value,
      ),
    }),
  );

  //setOsebeOptions in format for select component
  const osebeOptions = sifrantSetSelectValueAndLabel(
    osebeImenik,
    "kisid",
    "naziv",
  );

  //onSubmit function to call mutation for adding clanica UM to project
  const onSubmit = (data) => {
    const payload = {
      clanicaID: data.clanicaID,
      vlogaID: data.vlogaID,
      kisID: data.osebaSelected.value,
      skrbnik: data.skrbnik,
    };

    addClanicaNaProjekt(payload, {
      onSuccess: () => {
        setIsAdding(false);
      },
      onError: () => setTimeout(() => resetMutationError(), 3000),
    });
  };

  return (
    <Card className="mb-4 p-3 mt-4">
      <h4 className="form-section-heading">
        Dodaj sodelujočo članico na projekt
      </h4>
      <Form className="mt-2" onSubmit={handleSubmit((data) => onSubmit(data))}>
        <FormSelect
          label="Članica"
          name="clanicaID"
          control={control}
          options={claniceOptions}
          validationError={errors.clanicaID?.message}
        />
        <FormSelect
          label="Vloga članice"
          name="vlogaID"
          control={control}
          options={sifrantVlogaOptions}
          validationError={errors.vlogaID?.message}
        />
        <FormSelectSearchAsyncField
          label="Kontaktna oseba članice UM"
          name="osebaSelected"
          control={control}
          options={osebeOptions}
          setSearchTerm={setSearchTermOseba}
          placeholder="Iskanje kontaktne osebe..."
          isLoading={isLoadingOsebe}
          validationError={errors.osebaSelected?.message}
        />
        <FormField
          label="Izbrano članico določi za skrbnika projekta"
          formControlType="checkbox"
          name="skrbnik"
          register={register("skrbnik")}
        />

        <button
          type="submit"
          className="btn btn-primary mt-3"
          disabled={!isDirty || isPendingAddClanicaNaProjekt}
        >
          {isPendingAddClanicaNaProjekt ? "Shranjevanje..." : "Shrani"}
        </button>
        <button
          type="button"
          className="btn btn-secondary mt-3 ms-2 "
          onClick={() => setIsAdding(false)}
        >
          Prekliči
        </button>
        {isError && <ErrorMessage message={error.message} />}
      </Form>
    </Card>
  );
};

export default AddClanicoNaProjekt;
